import React from 'react';
import { Divider, Flex, HStack, TextStyle, VStack, Box, Button, Text } from '@cad-arc';
import { partition } from 'lodash';
import { DispatchAreaObject, DispatchAreaUserTypeEnum } from '@mark43/cad-api';
import { DispatchAreaManagementForm } from './types';
import { FormRadioGroup } from '../../forms/integrated-form-controls/form-radio-group';
import { useIntegratedForm } from '../../forms/use-integrated-form';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';

function SelectionTable(props: {
    title: string;
    showMonitorAll: boolean;
    dispatchAreaObjects: DispatchAreaObject[];
}): React.ReactElement {
    const { form, getGenericButtonProps } = useIntegratedForm<DispatchAreaManagementForm>();
    const allValues = form.watch();
    const notAllValuesAreMonitor = Object.values(allValues ?? []).some((value) => {
        return value !== 'MONITOR';
    });

    const { ownDispatchAreas } = useEnabledFeatures();

    const enabledFeatures = useEnabledFeatures();
    const radioGroupItems = React.useMemo(() => {
        return [
            ...(ownDispatchAreas
                ? [
                      {
                          value: DispatchAreaUserTypeEnum.OWNER.name,
                          label: 'Dispatch',
                      },
                  ]
                : []),
            {
                value: DispatchAreaUserTypeEnum.MONITOR.name,
                label: 'Monitor',
            },
            {
                value: 'NONE',
                label: 'Not Visible',
            },
        ];
    }, [ownDispatchAreas]);

    return (
        <Box w="100%">
            <HStack style={{ gap: '0px' }}>
                <Box w="110px">
                    <TextStyle fontWeight="bold">{props.title}</TextStyle>
                </Box>
                {enabledFeatures.radioChannels ? (
                    <Box w="51px">
                        <Text fontSize="13px">Radio</Text>
                    </Box>
                ) : undefined}
                <Box w="76px">
                    <Text fontSize="13px">Group</Text>
                </Box>
                <Box w="121px">
                    <Text fontSize="13px">Dispatcher</Text>
                </Box>
                <Box flexGrow={10} textAlign="right">
                    {props.showMonitorAll ? (
                        <Button
                            data-testid="monitor-unmonitor-all"
                            data-is-monitoring-all={!notAllValuesAreMonitor}
                            variant="ghost"
                            onClick={() => {
                                const existingValues = form.getValues();
                                if (notAllValuesAreMonitor) {
                                    for (const key of Object.keys(existingValues)) {
                                        form.setValue(key, 'MONITOR');
                                    }
                                } else {
                                    for (const key of Object.keys(existingValues)) {
                                        form.setValue(key, 'NONE');
                                    }
                                }
                            }}
                            {...getGenericButtonProps()}
                        >
                            {notAllValuesAreMonitor ? 'Monitor All' : 'Unmonitor All'}
                        </Button>
                    ) : null}
                </Box>
            </HStack>
            <Divider />
            {props.dispatchAreaObjects.map(
                (dispatchAreaObject: DispatchAreaObject, index: number) => (
                    <Box
                        key={dispatchAreaObject.id}
                        data-testid="manage-dispatch-areas-row"
                        data-row-index={index}
                    >
                        <HStack key={dispatchAreaObject.id} style={{ gap: 0 }} py="3px">
                            <Box w="110px">
                                <Text fontWeight="bold" fontSize="13px">
                                    {dispatchAreaObject.name ?? '--'}
                                </Text>
                            </Box>
                            {enabledFeatures.radioChannels ? (
                                <Box w="51px">
                                    <Text fontSize="13px">{dispatchAreaObject.radio ?? '--'}</Text>
                                </Box>
                            ) : undefined}
                            <Box w="76px">
                                <Text fontSize="13px">{dispatchAreaObject.group ?? '--'}</Text>
                            </Box>
                            <Box w="121px">
                                <Text fontSize="13px">{dispatchAreaObject.dispatcher ?? '--'}</Text>
                            </Box>
                            <Flex flexGrow={10} justifyContent="flex-end">
                                <FormRadioGroup
                                    data-testid="monitor-type"
                                    name={dispatchAreaObject.id.toString()}
                                    control={form.control}
                                    defaultValue={form.getValues(dispatchAreaObject.id.toString())}
                                    radioGroupProps={{
                                        size: 'md',
                                    }}
                                    items={radioGroupItems}
                                />
                            </Flex>
                        </HStack>
                        <Divider />
                    </Box>
                )
            )}
        </Box>
    );
}

export function SelectionTables(props: {
    dispatchAreas?: DispatchAreaObject[];
}): React.ReactElement {
    const [covered, notCovered] = partition(
        props.dispatchAreas,
        (dispatchArea) => dispatchArea.dispatcher
    );
    return (
        <VStack style={{ gap: '8px' }} data-testid="manage-dispatch-areas-selection-tables">
            <SelectionTable
                title="Not Covered"
                data-testid="manage-dispatch-areas-not-covered"
                dispatchAreaObjects={notCovered}
                showMonitorAll={true}
            />
            <SelectionTable
                title="Covered"
                data-testid="manage-dispatch-areas-covered"
                dispatchAreaObjects={covered}
                showMonitorAll={false}
            />
        </VStack>
    );
}
